/**
 * useSocket.js — Hostinger subdomain production version
 *
 * Reads REACT_APP_SOCKET_URL and REACT_APP_SOCKET_PATH from .env.production
 * Falls back to localhost for local development.
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

// On subdomain: both point to the same origin — simple and clean
const SOCKET_URL  = process.env.REACT_APP_SOCKET_URL  || 'http://localhost:5000';
const SOCKET_PATH = process.env.REACT_APP_SOCKET_PATH || '/socket.io';

export function SocketProvider({ children }) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      path: SOCKET_PATH,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // Try WebSocket first for low-latency lyric sync,
      // fall back to polling if WebSocket is blocked
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('[Socket] Connected:', socket.id);
      setConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.warn('[Socket] Connection error:', err.message);
    });

    socketRef.current = socket;

    return () => socket.disconnect();
  }, []);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}
